import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, doc, setDoc, runTransaction, getDoc, updateDoc, deleteDoc, query, where, onSnapshot } from "firebase/firestore";
import { storage, ref, uploadBytes, getDownloadURL } from './firebaseUtils.js';
import { Save, ArrowLeft, User, MapPin, Plus, Trash2, X, Edit2, Phone, Mail, FileText, UserCheck, Copy, Loader2, Paperclip, Eye, ChevronDown, AlertCircle, Clock, Package } from 'lucide-react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import useClickOutside from './hooks/useClickOutside.js';
import useUnsavedChanges from './hooks/useUnsavedChanges.js';
import Spinner from './Spinner.js';
import { NewCustomerModal, AddAddressModal } from './CustomerModals.js';
import HorizontalTimeline from './components/HorizontalTimeline.js';
import { useConfig } from './hooks/useConfig.js';


const formatAddress = (addr) => {
  if (!addr) return 'No Address Selected';
  return `${addr.address || "N/A"}, ${addr.city || "N/A"}, ${addr.state || "N/A"} - ${addr.pincode || "N/A"}`;
};

const getFirmPrefix = (firmId) => {
  if (firmId.startsWith('nexrise')) return 'NIIPL';
  if (firmId.startsWith('jagdish')) return 'JE';
  if (firmId.startsWith('cloudnine')) return 'C9';
  return 'GEN';
};

const getFileNameFromUrl = (url) => {
    if (!url) return '';
    try {
        const decodedUrl = decodeURIComponent(url);
        const pathParts = decodedUrl.split('?')[0].split('/');
        const fileNameWithPrefix = pathParts[pathParts.length - 1];
        const underscoreIndex = fileNameWithPrefix.indexOf('_');
        if (underscoreIndex !== -1) {
            if (/^\d{13,}/.test(fileNameWithPrefix)) {
                return fileNameWithPrefix.substring(underscoreIndex + 1);
            }
            const lastUnderscoreIndex = fileNameWithPrefix.lastIndexOf('_');
            if (lastUnderscoreIndex !== -1 && /\d{13,}/.test(fileNameWithPrefix.substring(lastUnderscoreIndex + 1))) {
                return fileNameWithPrefix.substring(0, lastUnderscoreIndex);
            }
        }
        return fileNameWithPrefix;
    } catch (e) {
        return 'Attached File';
    }
};

const OrderDetail = ({ db, user }) => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); 

  const customerSearchRef = useClickOutside(() => setShowCustSuggestions(false));
  const bomSearchRef = useClickOutside(() => setShowBomSearchSuggestions(false));

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  // --- BOM STATE ---
  const [boms, setBoms] = useState([]);
  const [showBomModal, setShowBomModal] = useState(false);
  const [currentBom, setCurrentBom] = useState(null); 
  const [bomSearchTerm, setBomSearchTerm] = useState('');
  const [bomFilteredProducts, setBomFilteredProducts] = useState([]);
  const [showBomSearchSuggestions, setShowBomSearchSuggestions] = useState(false);

  // --- 1. MAIN FORM STATE ---
  const [formData, setFormData] = useState({
    firmId: '',
    date: new Date().toISOString().split('T')[0],
    orderType: 'Sales', 
    salesPerson: '',
    subject: '',
    customerID: '',
    billingAddress: null,
    shippingAddress: null,
    projectName: '',
    projectLocation: null,
    quoteNumber: '',
    quoteDate: '',
    poNumber: '',
    poDate: '',
    orderValue: 0,
    paymentStatus: 'RECEIVED',
    salesOrderNo: '',
    proformaInvoiceNo: '',
    customerPONo: '',
    quoteFileUrl: '',
    poFileUrl: '',
    workOrderFileUrl: '',
    rentalAgreementFileUrl: '',
    securityChequeFileUrl: '',
    rentalAgreementNo: '',
    rentalPeriodStart: '',
    rentalPeriodEnd: '',
    monthlyRentalValue: 0,
    workOrderNumber: '',
    woDate: '',
    rentalAgreementReceived: false,
    backgroundVerificationDone: false,
    backgroundVerificationDoneBy: '',
    securityChequeReceived: false,
    rentalTenureMonths: 0,
    logisticsDetails: '',
    installationDetails: '',
    opsRejectionReason: '',
    opsHoldReason: '',
    finRejectionReason: '',
    finHoldReason: '',
  });

  const [materialChecklistState, setMaterialChecklistState] = useState({
    stickers: false,
    laserMarking: false,
    materialWrapping: false,
    materialInstructions: ''
  });

  // --- 2. DATA LOAD STATE ---
  const [firms, setFirms] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null); 
  const [bomDatabase, setBomDatabase] = useState([]);
  
  // --- 3. UI STATE ---
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustSuggestions, setShowCustSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dispatches, setDispatches] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false); 
  const [isDirty, setIsDirty] = useState(false);
  const isInitialRender = useRef(true);
  const originalOrderData = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false); 
  const [showOpsDropdown, setShowOpsDropdown] = useState(false); 

  // --- REJECTION STATE ---
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // --- HOLD STATE ---
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [otherHoldReason, setOtherHoldReason] = useState('');
  const [isHolding, setIsHolding] = useState(false);

  const setSafeToNavigate = useUnsavedChanges(isDirty);

  const handleRejectSubmit = async () => {
    if (!rejectionReason) {
      alert("Please select a rejection reason.");
      return;
    }
    if (rejectionReason === 'Other' && !otherReason.trim()) {
      alert("Please specify the other reason.");
      return;
    }

    const finalReason = rejectionReason === 'Other' ? otherReason : rejectionReason;

    if (!window.confirm(`Are you sure you want to reject this Order?`)) return;

    setIsRejecting(true);
    try {
      await updateDoc(doc(db, "orders", id), {
        status: 'ops_rejected',
        opsRejectionReason: finalReason,
        updatedAt: new Date().toISOString()
      });
      alert("Order rejected successfully.");
      setShowRejectModal(false);
      setRejectionReason('');
      setOtherReason('');
      // Update local state to reflect change immediately
      setFormData(prev => ({ ...prev, status: 'ops_rejected', opsRejectionReason: finalReason }));
    } catch (error) {
      alert("Error rejecting order: " + error.message);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleHoldSubmit = async () => {
    if (!holdReason) {
      alert("Please select a hold reason.");
      return;
    }
    if (holdReason === 'Other' && !otherHoldReason.trim()) {
      alert("Please specify the other reason.");
      return;
    }

    const finalReason = holdReason === 'Other' ? otherHoldReason : holdReason;

    if (!window.confirm(`Are you sure you want to put this Order on Hold?`)) return;

    setIsHolding(true);
    try {
      await updateDoc(doc(db, "orders", id), {
        status: 'ops_hold',
        opsHoldReason: finalReason,
        updatedAt: new Date().toISOString()
      });
      alert("Order put on hold successfully.");
      setShowHoldModal(false);
      setHoldReason('');
      setOtherHoldReason('');
      // Update local state
      setFormData(prev => ({ ...prev, status: 'ops_hold', opsHoldReason: finalReason }));
    } catch (error) {
      alert("Error putting order on hold: " + error.message);
    } finally {
      setIsHolding(false);
    }
  };

  // Track changes to form data in edit mode
  useEffect(() => {
    if (isEditing) {
      if (isInitialRender.current) {
        isInitialRender.current = false;
        return;
      }
      setIsDirty(true);
    } else {
      setIsDirty(false);
      isInitialRender.current = true;
    }
  }, [isEditing, formData, boms, materialChecklistState]);
  const [isEditingAllowed, setIsEditingAllowed] = useState(true); 

  // --- FILE MODAL STATE ---
  const [showFileModal, setShowFileModal] = useState(false);
  const dropdownRef = useClickOutside(() => setShowDropdown(false)); 
  const opsDropdownRef = useClickOutside(() => setShowOpsDropdown(false));
  const [activeFileField, setActiveFileField] = useState(null);
  const [activeFileLabel, setActiveFileLabel] = useState('');

  // --- 4. MODAL STATES ---
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressModalType, setAddressModalType] = useState('billing'); 
  const [showModal, setShowModal] = useState(false); 
  const [customerDataToEdit, setCustomerDataToEdit] = useState(null);
  const [addressDataToEdit, setAddressDataToEdit] = useState(null);

  // --- INITIAL DATA LOADING ---
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const firmSnap = await getDocs(collection(db, "firms"));
      const firmsList = firmSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFirms(firmsList);

      const custSnap = await getDocs(collection(db, "customers"));
      const customersList = custSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllCustomers(customersList);

      const bomSnap = await getDocs(collection(db, "bom_products"));
      const bomList = bomSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBomDatabase(bomList);

      if (id) { 
        const orderDocRef = doc(db, "orders", id);
        const orderDocSnap = await getDoc(orderDocRef);

        if (orderDocSnap.exists()) {
          const orderData = orderDocSnap.data();
          originalOrderData.current = { ...orderData, id: orderDocSnap.id };
          setFormData({
            ...orderData,
            firmId: orderData.firmId || "N/A",
            date: orderData.date || new Date().toISOString().split('T')[0],
            orderType: orderData.orderType || 'Sales',
            salesPerson: orderData.salesPerson || "N/A",
            subject: orderData.subject || "N/A",
            customerID: orderData.customerID || "N/A",
            billingAddress: orderData.billingAddress || null,
            shippingAddress: orderData.shippingAddress || null,
            projectName: orderData.projectName || "N/A",
            projectLocation: orderData.projectLocation || null,
            quoteNumber: orderData.quoteNumber || "N/A",
            quoteDate: orderData.quoteDate || "N/A",
            poNumber: orderData.poNumber || "N/A",
            poDate: orderData.poDate || "N/A",
            orderValue: orderData.orderValue || 0,
            paymentStatus: orderData.paymentStatus || 'RECEIVED',
            salesOrderNo: orderData.salesOrderNo || "N/A",
            proformaInvoiceNo: orderData.proformaInvoiceNo || "N/A",
            customerPONo: orderData.customerPONo || "N/A",
            quoteFileUrl: orderData.quoteFileUrl || orderData.quoteUrl || '',
            poFileUrl: orderData.poFileUrl || orderData.poUrl || '',
            workOrderFileUrl: orderData.workOrderFileUrl || orderData.workOrderUrl || '',
            rentalAgreementFileUrl: orderData.rentalAgreementFileUrl || orderData.rentalAgreementUrl || '',
            securityChequeFileUrl: orderData.securityChequeFileUrl || orderData.securityChequeUrl || '',
            rentalAgreementNo: orderData.rentalAgreementNo || "N/A",
            rentalPeriodStart: orderData.rentalPeriodStart || "N/A",
            rentalPeriodEnd: orderData.rentalPeriodEnd || "N/A",
            monthlyRentalValue: orderData.monthlyRentalValue || 0,
            workOrderNumber: orderData.workOrderNumber || "N/A",
            woDate: orderData.woDate || "N/A",
            rentalAgreementReceived: orderData.rentalAgreementReceived || false,
            backgroundVerificationDone: orderData.backgroundVerificationDone || false,
            backgroundVerificationDoneBy: orderData.backgroundVerificationDoneBy || "N/A",
            securityChequeReceived: orderData.securityChequeReceived || false,
            rentalTenureMonths: orderData.rentalTenureMonths || 0,
            logisticsDetails: orderData.logisticsDetails || "N/A",
            installationDetails: orderData.installationDetails || "N/A",
            opsRejectionReason: orderData.opsRejectionReason || '',
            opsHoldReason: orderData.opsHoldReason || '',
            finRejectionReason: orderData.finRejectionReason || '',
            finHoldReason: orderData.finHoldReason || '',
          });
          setBoms(orderData.boms || []);
          setMaterialChecklistState(orderData.materialChecklist || {
            stickers: false,
            laserMarking: false,
            materialWrapping: false,
            materialInstructions: ''
          });

          const customer = customersList.find(c => c.id === orderData.customerID);
          if (customer) {
            setSelectedCustomer(customer);
            setCustomerSearchTerm(customer.display_name);
          }
          // Lock editing if status is confirmed_order, pending_finance, or on hold
          setIsEditingAllowed(!['confirmed_order', 'pending_finance', 'moc_confirmed', 'ops_hold', 'fin_hold', 'packing_hold'].includes(orderData.status)); 
        } else {
          alert("Order not found!");
          navigate("/orders");
        }
      } 
      setLoading(false);
    };
    loadData();
  }, [db, id, navigate]);

  // Subscribe to dispatches for this order
  useEffect(() => {
    if (!id) return;
    const q = query(collection(db, "dispatches"), where("orderId", "==", id));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dispatchesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDispatches(dispatchesList);
    });
    return () => unsubscribe();
  }, [db, id]);

  // --- CUSTOMER SEARCH LOGIC ---
  const handleCustomerSearch = (value) => {
    setCustomerSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allCustomers.filter(c => c.display_name?.toLowerCase().includes(term));
      setFilteredCustomers(matches);
      setShowCustSuggestions(true);
    } else {
      setFilteredCustomers(allCustomers);
      setShowCustSuggestions(true);
    }
  };

  const handleFirmChange = (e) => {
    const firmId = e.target.value;
    const originalFirmId = originalOrderData.current?.firmId;
    
    let newOrderNumber = formData.orderNumber;
    if (firmId !== originalFirmId) {
        const newPrefix = getFirmPrefix(firmId);
        newOrderNumber = `${newPrefix}/25-26/OB---`;
    } else {
        newOrderNumber = originalOrderData.current?.orderNumber;
    }

    setFormData(p => ({ ...p, firmId, orderNumber: newOrderNumber }));
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer); 
    setFormData(prev => ({ 
      ...prev, 
      customerID: customer.id,
      billingAddress: customer.addresses?.find(a => a.address_type === 'Billing') || null,
      shippingAddress: customer.addresses?.find(a => a.address_type === 'Shipping') || null,
    }));
    setCustomerSearchTerm(customer.display_name);
    setShowCustSuggestions(false);
  };
  
  const handleAddressSelect = (type, value) => {
    if (value === 'new') {
      setAddressModalType(type);
      setShowAddressModal(true);
      return;
    }

    const address = selectedCustomer.addresses.find(a => a.id === value);
    if (type === 'billing') {
      setFormData(prev => ({ ...prev, billingAddress: address }));
    } else {
      setFormData(prev => ({ ...prev, shippingAddress: address }));
    }
  };

  const handleSaveOrUpdateOrder = async (orderStatus) => { 
    if (orderStatus === 'confirmed_order' && user?.role === 'sales') {
      const confirmMessage = "Are you sure you want to SUBMIT this order to Operations? \n\nPlease verify all details (Firm, Customer, Items, Value) before proceeding. \n\nOnce submitted, this order will be locked and CANNOT be edited. Do you wish to continue?";
      if (!window.confirm(confirmMessage)) return;
      orderStatus = 'in_que';
    }

    const requiredFields = {
      firmId: 'From (Firm)',
      date: 'Date',
      salesPerson: 'Salesperson',
      orderType: 'Order Type',
      customerID: 'Customer',
    };

    const missingFieldKeys = Object.keys(requiredFields).filter(field => {
      const value = formData[field];
      return !value || (typeof value === 'string' && value.trim() === '');
    });

    if (missingFieldKeys.length > 0) {
      const missingFieldNames = missingFieldKeys.map(key => requiredFields[key]).join(', ');
      alert(`Please fill out all required fields: ${missingFieldNames}.`);
      return;
    }

    setIsSaving(true);

    // CRITICAL: Force Sales-confirmed orders to Operations Queue
    const isSalesRole = user?.role?.toLowerCase() === 'sales';
    const finalStatus = (orderStatus === 'confirmed_order' && isSalesRole) ? 'in_que' : orderStatus;

    const bomsToSave = boms.map(bom => ({
      ...bom,
      components: bom.components.map(comp => {
        return {
          ...comp, 
          qty: Math.round(comp.baseQty * bom.multiplier), 
        };
      })
    }));

    try {
        let finalOrderNumber = formData.orderNumber;

        // If firm changed, generate new sequence number
        if (id && formData.firmId !== originalOrderData.current?.firmId) {
            const firmPrefix = getFirmPrefix(formData.firmId);
            const counterKey = 'order_' + firmPrefix.toLowerCase() + '_count';
            const sequencesRef = doc(db, "counters", "sequences");
            const yearCode = "25-26";

            const newCounter = await runTransaction(db, async (transaction) => {
                const sequenceDoc = await transaction.get(sequencesRef);
                if (!sequenceDoc.exists()) throw new Error("Counter document does not exist!");
                const currentCount = sequenceDoc.data()[counterKey] || 1000;
                const nextCount = currentCount + 1;
                transaction.update(sequencesRef, { [counterKey]: nextCount });
                return currentCount;
            });
            finalOrderNumber = `${firmPrefix}/${yearCode}/OB${newCounter}`;
        }

        const orderPayload = {
            ...formData,
            orderNumber: finalOrderNumber,
            customerName: selectedCustomer ? selectedCustomer.display_name : 'N/A',
            logisticsDetails: formData.logisticsDetails,
            installationDetails: formData.installationDetails,
            materialChecklist: materialChecklistState,
            boms: bomsToSave, 
            status: finalStatus,
            updatedAt: new Date().toISOString()
        };

        if (id) { 
            const orderDocRef = doc(db, "orders", id);
            await updateDoc(orderDocRef, orderPayload);
            alert(`Order Updated Successfully! Status: ${finalStatus.replace('_', ' ').toUpperCase()}`);
            originalOrderData.current = { ...orderPayload, id }; // Update ref after successful save
            setFormData(prev => ({ ...prev, ...orderPayload, orderNumber: finalOrderNumber })); 
            setIsDirty(false);
            setSafeToNavigate();
            setIsEditing(false);
            if (finalStatus === 'confirmed_order' || finalStatus === 'in_que' || finalStatus === 'moc_confirmed') {
                setIsEditingAllowed(false);
            }
            setIsSaving(false);
        } else { 
            const firmPrefix = getFirmPrefix(formData.firmId);
            const counterKey = 'order_' + firmPrefix.toLowerCase() + '_count';
            const sequencesRef = doc(db, "counters", "sequences");
            const yearCode = "25-26";

            const newCounter = await runTransaction(db, async (transaction) => {
                const sequenceDoc = await transaction.get(sequencesRef);
                if (!sequenceDoc.exists()) throw new Error("Counter document does not exist!");
                const currentCount = sequenceDoc.data()[counterKey] || 1000;
                const nextCount = currentCount + 1;
                transaction.update(sequencesRef, { [counterKey]: nextCount });
                return currentCount;
            });

            orderPayload.orderNumber = `${firmPrefix}/${yearCode}/OB${newCounter}`;
            orderPayload.createdAt = new Date().toISOString();
            await addDoc(collection(db, "orders"), orderPayload);
            alert(`New Order Created Successfully! Number: ${orderPayload.orderNumber}`);
            setIsDirty(false);
            setSafeToNavigate();
            navigate('/orders'); 
        }
    } catch (error) {
        setIsSaving(false);
        console.error("Error saving/updating order: ", error);
        alert("Error saving/updating order: " + error.message);
    }
  };

  const handleClone = () => {
    const clonedOrder = { ...formData, boms, materialChecklist: materialChecklistState };
    delete clonedOrder.orderNumber;
    delete clonedOrder.salesOrderNo;
    delete clonedOrder.proformaInvoiceNo;
    delete clonedOrder.status;
    delete clonedOrder.createdAt;
    delete clonedOrder.updatedAt;

    navigate('/orders/new', { state: { clonedOrder: clonedOrder } });
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete this Order? This action cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, "orders", id));
        alert('Order deleted successfully.');
        navigate('/orders');
      } catch (error) {
        console.error("Error deleting order: ", error);
        alert('Failed to delete order: ' + error.message);
      }
    }
  };

  const handleExportPdf = () => {
    const orderForPdf = {
        ...formData,
        boms: boms,
        customerName: selectedCustomer.display_name,
        customerCompanyName: selectedCustomer.company_name,
        customerPaymentTerms: selectedCustomer.payment_terms,
    };
    
    if (!orderForPdf || firms.length === 0) {
        alert('Data is not ready yet. Please wait a moment.');
        return;
    }

    const selectedFirm = firms.find(f => f.id === orderForPdf.firmId);
    if (!selectedFirm) {
        alert('Could not find firm details for this order.');
        return;
    }
    
    const bomsHtml = orderForPdf.boms.map(bom => {
        const componentRows = bom.components.map((component, index) => `
            <tr class="item-row">
                <td>${index + 1}</td>
                <td>${component.sku || "N/A"}</td>
                <td contenteditable="true">${component.name}</td>
                <td class="text-left">${component.baseQty || 0}</td>
                <td class="text-left">${Math.round(component.baseQty * bom.multiplier)}</td>
            </tr>
        `).join('');

        const totalQty = bom.components.reduce((sum, comp) => sum + Math.round((comp.baseQty || 0) * (bom.multiplier || 1)), 0);

        return `
            <h2 style="margin-top: 20px; margin-bottom: 10px;">${bom.name}</h2>
            <table class="item-table">
                <thead>
                    <tr>
                        <th>Sr. No.</th>
                        <th>SKU</th>
                        <th style="width: 50%;">Item</th>
                        <th class="text-right">Quantity Per Set</th>
                        <th class="text-right">Total Qty</th>
                    </tr>
                </thead>
                <tbody>
                    ${componentRows}
                    <tr style="font-weight: bold; background-color: #f2f2f2;">
                        <td colspan="4" style="text-align: right; border: 1px solid #1b1b1b; padding: 8px;">Total Quantity:</td>
                        <td class="text-right" style="border: 1px solid #1b1b1b; padding: 8px;">${totalQty}</td>
                    </tr>
                </tbody>
            </table>
        `;
    }).join('');

    const formattedOrderDate = new Date(orderForPdf.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const formattedQuoteDate = orderForPdf.quoteDate ? new Date(orderForPdf.quoteDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

    const embeddedCss = `
        .container { width: 1000px; margin: 0 auto; font-family: Arial, sans-serif; color: #1b1b1b; }
        table { width: 100%; border-collapse: collapse; }
        .header-table td { padding: 10px; vertical-align: middle; }
        .header-table { border: 1px solid #1b1b1b; }
        .header-table p { font-size: 25px; font-weight: bold; margin: 0; }
        .logo { border-right: 1px solid #1b1b1b; width: 20%; }
        .firm-details { font-size: 14px; }
        .firm-details strong { font-size: 16px; }
        .Upper-table td { padding: 8px; text-align: left; border: 1px solid #1b1b1b; font-size: 16px; }
        .Upper-table td strong { white-space: nowrap; }
        .Upper-table td:nth-child(1), .Upper-table td:nth-child(3) { width: 1%; white-space: nowrap; }
        .Upper-table td[colspan] { width: auto; white-space: nowrap; }
        .table-titles { background-color: #f2f2f2; font-weight: bold; }
        .item-table th, .item-table td { border: 1px solid #1b1b1b; padding: 8px; vertical-align: top; }
        .item-table th { background-color: #f9f9f9; text-align: left; font-size: 16px; }
        .item-table td { font-size: 16px; }
        .text-right { text-align: right; }
        .tnc-table { border: 1px solid #1b1b1b; margin-top: 20px; }
        .terms-conditions { width: 70%; padding: 15px; font-size: 12px; border-right: 1px solid #1b1b1b; }
        .signature-area { width: 30%; text-align: right; padding: 15px; vertical-align: bottom; }
        .doc-title p { margin: 0; }
        .bom-title { 
            background-color: #e5e7eb; 
            padding: 10px; 
            border: 1px solid #1b1b1b; 
            border-bottom: none; 
            margin: 30px 0 0 0; 
            font-size: 14px; 
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-weight: bold;
        }
        .summary-table { border: 1px solid #1b1b1b; margin-top: 20px; width: 100%; }
        .summary-table td { padding: 12px; font-size: 16px; border: 1px solid #1b1b1b; }
        ul { margin: 0; padding-left: 20px; }
        li { margin-bottom: 4px; }
    `;

    const orderHtml = `
      <!DOCTYPE html><html><head><title>${orderForPdf.orderType} Order ${orderForPdf.orderNumber || id}_${orderForPdf.customerName}</title><style>${embeddedCss}</style></head>
      <body>
          <div class="container">
            <table class="header-table">
                <tr>
                    <td class="logo" style="width: 20%;"><img width="150px" src="${selectedFirm.logo}" alt="${selectedFirm.name} Logo"></td>
                    <td class="firm-details" style="width: 50%">
                        <strong>${selectedFirm.name}</strong>,<br>
                        ${selectedFirm.address}<br>
                        GSTIN - ${selectedFirm.gstin}<br>
                        E-Mail - ${selectedFirm.email}<br>
                        Tel: ${selectedFirm.tel}
                    </td>
                    <td style="text-align: right; vertical-align: top;" class="doc-title"><p>${orderForPdf.orderType.toUpperCase()} ORDER</p>${orderForPdf.orderNumber || id}</td>
                </tr>
            </table>
            <table class="Upper-table">
            <tr class="table-titles">
                  <td colspan="4" style="border-right:1px; solid #1b1b1b;"><strong>Order Details:</strong></td>
              </tr>
              <tr>
                <td><strong>Date:</strong></td><td>${formattedOrderDate}</td>
                <td><strong>Quote Number:</strong></td><td>${orderForPdf.quoteNumber || "N/A"}</td>
              </tr>
              <tr>
                <td><strong>Order ID:</strong></td><td>${orderForPdf.orderNumber || id}</td>
                <td><strong>Quote Date:</strong></td><td>${formattedQuoteDate}</td>
              </tr>
              <tr>
                <td><strong>Salesperson:</strong></td><td>${orderForPdf.salesPerson}</td>
                <td><strong>${orderForPdf.orderType === 'Sales' ? 'PO Number' : 'Work Order Number'}:</strong></td><td>${orderForPdf.orderType === 'Sales' ? orderForPdf.poNumber : orderForPdf.workOrderNumber || ''}</td>
              </tr>
              <tr>
                <td><strong>Project Name:</strong></td><td>${orderForPdf.projectName || ''}</td>
                <td><strong>${orderForPdf.orderType === 'Sales' ? 'PO Date' : 'Work Order Date'}:</strong></td><td>${orderForPdf.orderType === 'Sales' ? orderForPdf.poDate : orderForPdf.woDate || ''}</td>
              </tr>
              <tr>
                  <td><strong>Project Location:</strong></td><td>${orderForPdf.projectLocation?.address || ''}</td>
                  <td><strong>Rental Tenure:</strong></td><td>${orderForPdf.orderType === 'Rental' ? (orderForPdf.rentalTenureMonths + ' Months') : 'N/A'}</td>
              </tr>
              <tr>
                  <td><strong>Rental Agreement Status:</strong></td><td>${orderForPdf.orderType === 'Rental' ? (orderForPdf.rentalAgreementReceived ? 'Received' : 'Pending') : 'N/A'}</td>
                  <td><strong>Payment Terms:</strong></td><td>${orderForPdf.customerPaymentTerms || 'N/A'}</td>
              </tr>
              <tr>
                  <td><strong>Security Cheque Status:</strong></td><td>${orderForPdf.orderType === 'Rental' ? (orderForPdf.securityChequeReceived ? 'Received' : 'Pending') : 'N/A'}</td>
                  <td><strong>Background Verification:</strong></td><td>${orderForPdf.orderType === 'Rental' ? (orderForPdf.backgroundVerificationDone ? `Done (${orderForPdf.backgroundVerificationDoneBy})` : 'Pending') : 'N/A'}</td>
              </tr>
              <tr class="table-titles">
                  <td style="width: 33%;"><strong>Logistics Details</strong></td>
                  <td style="width: 33%;"><strong>Installation Details</strong></td>
                  <td style="width: 34%;" colspan="2"><strong>Material Checklist</strong></td>
              </tr>
              <tr>
                  <td style="vertical-align: top;">${orderForPdf.logisticsDetails || 'N/A'}</td>
                  <td style="vertical-align: top;">${orderForPdf.installationDetails || 'N/A'}</td>
                  <td style="vertical-align: top;" colspan="2">
                    <ul style="margin: 0; padding-left: 20px;">
                      <li>Stickers: ${orderForPdf.materialChecklist?.stickers ? 'Yes' : 'No'}</li>
                      <li>Laser Marking: ${orderForPdf.materialChecklist?.laserMarking ? 'Yes' : 'No'}</li>
                      <li>Material Wrapping: ${orderForPdf.materialChecklist?.materialWrapping ? 'Yes' : 'No'}</li>
                      ${orderForPdf.materialChecklist?.materialInstructions ? `<li>Instructions: ${orderForPdf.materialChecklist.materialInstructions}</li>` : ''}
                    </ul>
                  </td>
              </tr>
              <tr class="table-titles">
                  <td colspan="2" style="border-right:1px solid #1b1b1b;"><strong>Billing Details:</strong></td>
                  <td colspan="2"><strong>Shipping Details:</strong></td>
              </tr>
              <tr>
                  <td><strong>Company Name</strong></td><td>${orderForPdf.customerCompanyName || orderForPdf.customerName}</td>
                  <td><strong>Ship to:</strong></td><td>${orderForPdf.shippingAddress?.attention || orderForPdf.customerName}</td>
              </tr>
              <tr>
                  <td><strong>Address</strong></td><td>${formatAddress(orderForPdf.billingAddress)}</td>   
                  <td><strong>Address</strong></td><td>${formatAddress(orderForPdf.shippingAddress)}</td>
              </tr>
              <tr>
                  <td><strong>Contact Person</strong></td><td>${orderForPdf.billingAddress?.attention || ''}</td>
                  <td><strong>Contact Person</strong></td><td>${orderForPdf.shippingAddress?.attention || ''}</td>
              </tr>
              <tr>
                  <td><strong>Contact Number</strong></td><td>${orderForPdf.billingAddress?.phone || ''}</td>
                  <td><strong>Contact Number</strong></td><td>${orderForPdf.shippingAddress?.phone || ''}</td>
              </tr>
              <tr>
                  <td><strong>Contact Email</strong></td><td>${orderForPdf.billingAddress?.email || ''}</td>
                  <td><strong>Contact Email</strong></td><td>${orderForPdf.shippingAddress?.email || ''}</td>
              </tr>
              
            </table>
            <table class="summary-table">
                <tbody>
                    <tr class="total-row">
                       <td style="font-weight:bold; text-align: right;">Total Order Value:<br><strong>Rs.${(orderForPdf.orderValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                        <td style="font-weight:bold; text-align: right;">Payment Status:<br> ${(orderForPdf.paymentStatus || "N/A")}</td>
                    </tr>
                </tbody>
            </table>

            ${bomsHtml}
          </div>
      </body></html>
    `;

    const newWindow = window.open();
    newWindow.document.write(orderHtml);
    newWindow.document.close();
    newWindow.focus();
    newWindow.addEventListener('afterprint', () => {
        newWindow.close();
    }, { once: true });
    newWindow.print();
  }

  const renderPreviewContent = () => {
    const selectedFirm = firms.find(f => f.id === formData.firmId);
    if (!selectedFirm || !selectedCustomer) return <div style={{padding: '20px', textAlign: 'center'}}><Spinner /></div>;

    const formattedOrderDate = new Date(formData.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const formattedQuoteDate = formData.quoteDate ? new Date(formData.quoteDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
    const formattedWoDate = formData.woDate ? new Date(formData.woDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
    const formattedPoDate = formData.poDate ? new Date(formData.poDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

    return (
      <div style={{ 
        backgroundColor: 'white', 
        padding: isMobile ? '10px' : '40px', 
        borderRadius: isMobile ? '0' : '10px', 
        boxShadow: isMobile ? 'none' : '0 4px 6px rgba(0,0,0,0.1)', 
        fontFamily: 'Arial, sans-serif', 
        color: '#1b1b1b', 
        maxWidth: '1000px', 
        width: isMobile ? 'calc(100% + 20px)' : '100%',
        margin: isMobile ? '0 -10px' : '0 auto', 
        overflowX: isMobile ? 'auto' : 'visible',
        boxSizing: 'border-box'
      }}>
        <div style={{ minWidth: isMobile ? '800px' : 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #1b1b1b' }}>
          <tbody>
            <tr>
              <td style={{ padding: '10px', verticalAlign: 'middle', borderRight: '1px solid #1b1b1b', width: '20%' }}>
                <img width="150px" src={selectedFirm.logo} alt="Logo" />
              </td>
              <td style={{ padding: '10px', fontSize: '14px', width: '50%' }}>
                <strong style={{ fontSize: '16px' }}>{selectedFirm.name}</strong><br />
                {selectedFirm.address}<br />
                GSTIN - {selectedFirm.gstin}<br />
                E-Mail - {selectedFirm.email}<br />
                Tel: {selectedFirm.tel}
              </td>
              <td style={{ padding: '10px', textAlign: 'right', verticalAlign: 'top' }}>
                <p style={{ fontSize: '25px', fontWeight: 'bold', margin: 0 }}>{formData.orderType.toUpperCase()} ORDER</p>
                {formData.orderNumber || id}
              </td>
            </tr>
          </tbody>
        </table>

        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #1b1b1b', borderTop: 'none' }}>
          <tbody>
            <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
              <td colSpan="4" style={{ padding: '8px', border: '1px solid #1b1b1b' }}>Order Details:</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Date:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formattedOrderDate}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Quote Number:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.quoteNumber || ''}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Order ID:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.orderNumber || id}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Quote Date:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formattedQuoteDate}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Salesperson:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.salesPerson}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>{formData.orderType === 'Sales' ? 'PO Number' : 'Work Order Number'}:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.orderType === 'Sales' ? formData.poNumber : formData.workOrderNumber || ''}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Project Name:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.projectName || ''}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>{formData.orderType === 'Sales' ? 'PO Date' : 'Work Order Date'}:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.orderType === 'Sales' ? formattedPoDate : formattedWoDate || ''}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Project Location:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.projectLocation?.address || ''}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Rental Tenure:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.orderType === 'Rental' ? (formData.rentalTenureMonths + ' Months') : 'N/A'}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Rental Agreement:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.orderType === 'Rental' ? (formData.rentalAgreementReceived ? 'Received' : 'Pending') : 'N/A'}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Payment Terms:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{selectedCustomer?.payment_terms || 'N/A'}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Security Cheque:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.orderType === 'Rental' ? (formData.securityChequeReceived ? 'Received' : 'Pending') : 'N/A'}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Background Verif:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.orderType === 'Rental' ? (formData.backgroundVerificationDone ? `Done (${formData.backgroundVerificationDoneBy})` : 'Pending') : 'N/A'}</td>
            </tr>
            
            <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b', width: '33%' }}>Logistics Details</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b', width: '33%' }}>Installation Details</td>
              <td colSpan="2" style={{ padding: '8px', border: '1px solid #1b1b1b', width: '34%' }}>Material Checklist</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b', verticalAlign: 'top' }}>{formData.logisticsDetails || 'N/A'}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b', verticalAlign: 'top' }}>{formData.installationDetails || 'N/A'}</td>
              <td colSpan="2" style={{ padding: '8px', border: '1px solid #1b1b1b', verticalAlign: 'top' }}>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Stickers: {materialChecklistState.stickers ? 'Yes' : 'No'}</li>
                  <li>Laser Marking: {materialChecklistState.laserMarking ? 'Yes' : 'No'}</li>
                  <li>Material Wrapping: {materialChecklistState.materialWrapping ? 'Yes' : 'No'}</li>
                  {materialChecklistState.materialInstructions && <li>Instructions: {materialChecklistState.materialInstructions}</li>}
                </ul>
              </td>
            </tr>

            <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
              <td colSpan="2" style={{ padding: '8px', border: '1px solid #1b1b1b' }}>Billing Details:</td>
              <td colSpan="2" style={{ padding: '8px', border: '1px solid #1b1b1b' }}>Shipping Details:</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Company</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{selectedCustomer?.company_name || selectedCustomer?.display_name}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Ship to:</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.shippingAddress?.attention || selectedCustomer?.display_name}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Address</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formatAddress(formData.billingAddress)}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Address</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formatAddress(formData.shippingAddress)}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Contact Person</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.billingAddress?.attention || ''}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Contact Person</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.shippingAddress?.attention || ''}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Contact Number</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.billingAddress?.phone || ''}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Contact Number</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.shippingAddress?.phone || ''}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Contact Email</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.billingAddress?.email || ''}</td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}><strong>Contact Email</strong></td>
              <td style={{ padding: '8px', border: '1px solid #1b1b1b' }}>{formData.shippingAddress?.email || ''}</td>
            </tr>
          </tbody>
        </table>

        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #1b1b1b', marginTop: '20px' }}>
          <tbody>
            <tr>
              <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #1b1b1b' }}>
                Total Order Value:<br />
                <strong>Rs.{(formData.orderValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              </td>
              <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #1b1b1b', width: '30%' }}>
                Payment Status:<br />
                <strong>{formData.paymentStatus || 'N/A'}</strong>
              </td>
            </tr>
          </tbody>
        </table>

        {boms.map((bom, idx) => (
          <div key={idx} style={{ marginTop: '30px' }}>
            <h2 style={{ margin: '0 0 10px 0', textTransform: 'uppercase', fontWeight: 'bold' }}>{bom.name}</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #1b1b1b' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9f9f9' }}>
                  <th style={{ border: '1px solid #1b1b1b', padding: '8px', textAlign: 'left', width: '50px' }}>Sr.</th>
                  <th style={{ border: '1px solid #1b1b1b', padding: '8px', textAlign: 'left', width: '120px' }}>SKU</th>
                  <th style={{ border: '1px solid #1b1b1b', padding: '8px', textAlign: 'left' }}>Item</th>
                  <th style={{ border: '1px solid #1b1b1b', padding: '8px', textAlign: 'right', width: '100px' }}>Qty/Set</th>
                  <th style={{ border: '1px solid #1b1b1b', padding: '8px', textAlign: 'right', width: '100px' }}>Total Qty</th>
                </tr>
              </thead>
              <tbody>
                {bom.components.map((comp, cIdx) => (
                  <tr key={cIdx}>
                    <td style={{ border: '1px solid #1b1b1b', padding: '8px' }}>{cIdx + 1}</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '8px' }}>{comp.sku || ''}</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '8px' }}>{comp.name}</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '8px', textAlign: 'right' }}>{comp.baseQty || 0}</td>
                    <td style={{ border: '1px solid #1b1b1b', padding: '8px', textAlign: 'right' }}>{Math.round(comp.baseQty * bom.multiplier)}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
                  <td colSpan="4" style={{ border: '1px solid #1b1b1b', padding: '8px', textAlign: 'right' }}>Total Quantity:</td>
                  <td style={{ border: '1px solid #1b1b1b', padding: '8px', textAlign: 'right' }}>
                    {bom.components.reduce((sum, comp) => sum + Math.round((comp.baseQty || 0) * bom.multiplier), 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
        </div>
      </div>
    );
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const storageRef = ref(storage, `uploads/${fieldName}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setFormData(prev => ({ ...prev, [fieldName]: url }));
    } catch (error) {
      console.error("Upload failed", error);
      alert("Upload failed: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const openFileModal = (fieldName, label) => {
      setActiveFileField(fieldName);
      setActiveFileLabel(label);
      setShowFileModal(true);
  };

  const renderFileUpload = (fieldName, label) => (
    <div>
        <label style={labelStyle}>{label}</label>
        <button 
            onClick={() => openFileModal(fieldName, label)}
            style={{
                ...inputStyle, 
                textAlign: 'left', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: formData[fieldName] ? '#f0fdf4' : 'white',
                borderColor: formData[fieldName] ? '#22c55e' : '#d1d5db'
            }}
        >
            <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                {formData[fieldName] ? <Eye size={16} color="#16a34a"/> : <Paperclip size={16} color="#6b7280"/>}
                {formData[fieldName] ? 'View Attached File' : 'Attach File'}
            </span>
            {formData[fieldName] && <span style={{fontSize: '10px', color: '#16a34a', fontWeight: 'bold'}}>UPLOADED</span>}
        </button>
    </div>
  );

  if (loading) return <Spinner />; 

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: '100px' }}>
      
      {isSaving && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div style={{ textAlign: 'center' }}>
            <Spinner />
            <div style={{ marginTop: '10px', fontWeight: 'bold', color: '#2563eb' }}>Processing Order...</div>
          </div>
        </div>
      )}

      {/* REJECTION BANNER */}
      {formData.status === 'ops_rejected' && (
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #ef4444', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <AlertCircle size={24} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#991b1b', fontSize: '16px' }}>Order Returned by Operations</h3>
            <p style={{ margin: 0, color: '#b91c1c', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Reason:</strong> {formData.opsRejectionReason || 'No specific reason provided.'}
            </p>
            {isEditingAllowed && (
              <p style={{ margin: '10px 0 0 0', color: '#7f1d1d', fontSize: '13px' }}>
                Please review the feedback, update the necessary details below, and resubmit the order to move it back into the operations queue.
              </p>
            )}
          </div>
        </div>
      )}

      {/* HOLD BANNER */}
      {formData.status === 'ops_hold' && (
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <Clock size={24} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#b45309', fontSize: '16px' }}>Order On Hold (Operations)</h3>
            <p style={{ margin: 0, color: '#92400e', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Reason:</strong> {formData.opsHoldReason || 'Awaiting clarification or stock.'}
            </p>
            {user?.role === 'ops' && (
              <p style={{ margin: '10px 0 0 0', color: '#78350f', fontSize: '13px' }}>
                This order is paused. You can still view details or create an MOC when you are ready to proceed.
              </p>
            )}
          </div>
        </div>
      )}

      {/* FIN REJECTION BANNER */}
      {formData.status === 'fin_rejected' && (
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #ef4444', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <AlertCircle size={24} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#991b1b', fontSize: '16px' }}>Order Returned by Finance</h3>
            <p style={{ margin: 0, color: '#b91c1c', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Reason:</strong> {formData.finRejectionReason || 'No specific reason provided.'}
            </p>
            {isEditingAllowed && (
              <p style={{ margin: '10px 0 0 0', color: '#7f1d1d', fontSize: '13px' }}>
                Please review the feedback, update the necessary details below, and resubmit the order to move it back into the operations queue.
              </p>
            )}
          </div>
        </div>
      )}

      {/* FIN HOLD BANNER */}
      {formData.status === 'fin_hold' && (
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <Clock size={24} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#b45309', fontSize: '16px' }}>Order On Hold (Finance)</h3>
            <p style={{ margin: 0, color: '#92400e', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Reason:</strong> {formData.finHoldReason || 'Awaiting finance clarification.'}
            </p>
            {user?.role === 'ops' && (
              <p style={{ margin: '10px 0 0 0', color: '#78350f', fontSize: '13px' }}>
                This order is paused. You can still view details but cannot proceed until finance resolves the hold.
              </p>
            )}
          </div>
        </div>
      )}

      {/* PACKING HOLD BANNER */}
      {formData.status === 'packing_hold' && (
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <Clock size={24} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#b45309', fontSize: '16px' }}>Order On Hold (Packing)</h3>
            <p style={{ margin: 0, color: '#92400e', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Reason:</strong> {formData.packingHoldReason || 'Awaiting warehouse clarification.'}
            </p>
            {user?.role === 'sales' && (
              <p style={{ margin: '10px 0 0 0', color: '#78350f', fontSize: '13px' }}>
                The warehouse team has put this order on hold. Please review the reason.
              </p>
            )}
          </div>
        </div>
      )}

      {/* PACKING REJECTION BANNER */}
      {formData.status === 'packing_rejected' && (
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #ef4444', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '15px', animation: 'fadeIn 0.3s' }}>
          <AlertCircle size={24} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#991b1b', fontSize: '16px' }}>Order Returned by Warehouse</h3>
            <p style={{ margin: 0, color: '#b91c1c', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Reason:</strong> {formData.packingRejectionReason || 'No specific reason provided.'}
            </p>
            {isEditingAllowed && (
              <p style={{ margin: '10px 0 0 0', color: '#7f1d1d', fontSize: '13px' }}>
                Please review the feedback, update the necessary details below, and resubmit the order to move it back into the operations queue.
              </p>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Link to="/orders" style={{ color: '#6b7280' }}><ArrowLeft size={20} /></Link>
          <h2 style={{ margin: 0 }}>{isEditing ? 'Edit Order' : 'Order Preview'} #{formData.orderNumber || id}</h2>
          {!isEditing && (
             <span style={{ 
               padding: '4px 12px', 
               borderRadius: '20px', 
               fontSize: '12px', 
               fontWeight: 'bold',
               textTransform: 'uppercase',
               backgroundColor: formData.status === 'draft' ? '#fefce8' : 
                                  formData.status === 'in_que' ? '#e0e7ff' :
                                  formData.status === 'ops_hold' ? '#fef3c7' :
                                  formData.status === 'confirmed_order' ? '#dcfce7' : 
                                  formData.status === 'packing' ? '#ede9fe' : 
                                  formData.status === 'shipped' ? '#e0f2fe' :
                                  formData.status === 'in_transit' ? '#fff7ed' :
                                  formData.status === 'delivered' ? '#d1fae5' :
                                  formData.status === 'partially_shipped' ? '#e0e7ff' : 
                                  formData.status === 'canceled' ? '#fee2e2' : '#f3f4f6',
                                  
               color: formData.status === 'draft' ? '#a16207' :
                      formData.status === 'in_que' ? '#4338ca' :
                      formData.status === 'ops_hold' ? '#b45309' :
                      formData.status === 'confirmed_order' ? '#166534' : 
                      formData.status === 'packing' ? '#5b21b6' : 
                      formData.status === 'shipped' ? '#075985' :
                      formData.status === 'in_transit' ? '#c2410c' :
                      formData.status === 'delivered' ? '#047857' :
                      formData.status === 'partially_shipped' ? '#4338ca' :
                      formData.status === 'canceled' ? '#dc2626' : '#374151'
             }}>
               {formData.status === 'draft' ? 'Draft' : 
                formData.status === 'in_que' ? 'In Que' :
                formData.status === 'ops_hold' ? 'On Hold' :
                formData.status === 'confirmed_order' ? 'Confirmed Order' :
                formData.status === 'packing' ? 'Packing' : 
                formData.status === 'shipped' ? 'Shipped' :
                formData.status === 'in_transit' ? 'In Transit' :
                formData.status === 'delivered' ? 'Delivered' :
                formData.status === 'canceled' ? 'Canceled' :
                formData.status === 'partially_shipped' ? 'Partially Shipped' :
                formData.status}
             </span>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {isEditing ? (
            <>
              <button onClick={() => setIsEditing(false)} style={{ ...btnPrimaryStyle, backgroundColor: '#6b7280' }}>Cancel</button>
              <button onClick={() => handleSaveOrUpdateOrder('draft')} style={{ ...btnPrimaryStyle, backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db' }}>
                <Save size={18} color="black" /> Save Draft
              </button>
              <button onClick={() => handleSaveOrUpdateOrder('confirmed_order')} style={{ ...btnPrimaryStyle, backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db' }}>
                <Save size={18} color="black" /> Confirm Order
              </button>
            </>
          ) : (
            <>
              {isEditingAllowed && user?.role === 'sales' && (
                <button onClick={() => setIsEditing(true)} style={{ ...btnPrimaryStyle, backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db' }}>
                  <Edit2 size={18} color="black" /> Edit
                </button>
              )}
              {!['confirmed_order', 'in_que', 'moc_confirmed', 'ops_hold', 'fin_hold', 'packing_hold'].includes(formData.status) && user?.role === 'sales' && (
                <button onClick={() => handleSaveOrUpdateOrder('confirmed_order')} style={{ ...btnPrimaryStyle, backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db' }}>
                  <Save size={18} color="black" /> Submit
                </button>
              )}
              {user?.role === 'sales' && (
                <button onClick={handleExportPdf} style={{ ...btnPrimaryStyle, backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db' }}>
                  <FileText size={18} color="black" /> Export PDF
                </button>
              )}
              
              {user?.role === 'sales' && (
                <div style={{ position: 'relative' }} ref={dropdownRef}>
                  <button onClick={() => setShowDropdown(!showDropdown)} style={{ ...btnPrimaryStyle, backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}>
                    More <ChevronDown size={18} />
                  </button>
                  {showDropdown && (
                    <div style={{ ...dropdownStyle, top: 'calc(100% + 5px)', right: 0, left: 'auto', width: '200px', zIndex: 1001 }}>
                      <div onClick={() => { handleClone(); setShowDropdown(false); }} style={dropdownItemStyle}>
                        <Copy size={16} style={{ marginRight: '10px' }} /> Clone Order
                      </div>
                      <div onClick={() => { handleDelete(); setShowDropdown(false); }} style={{ ...dropdownItemStyle, color: '#ef4444' }}>
                        <Trash2 size={16} style={{ marginRight: '10px' }} /> Delete Order
                      </div>
                    </div>
                  )}
                </div>
              )}

              {user?.role === 'ops' && !isEditing && formData.status !== 'draft' && (
                <div style={{ position: 'relative' }} ref={opsDropdownRef}>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {(formData.status === 'confirmed_order' || formData.status === 'in_que' || formData.status === 'ops_hold') && (
                      <button 
                        onClick={() => navigate('/ops/moc/new', { state: { orderId: id } })}
                        style={{ ...btnPrimaryStyle, backgroundColor: '#059669', color: 'white' }}
                      >
                        <Plus size={18} /> Create MOC
                      </button>
                    )}
                    <button onClick={() => setShowOpsDropdown(!showOpsDropdown)} style={{ ...btnPrimaryStyle, backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}>
                      More <ChevronDown size={18} />
                    </button>
                  </div>
                  {showOpsDropdown && (
                    <div style={{ ...dropdownStyle, top: 'calc(100% + 5px)', right: 0, left: 'auto', width: '200px', zIndex: 1001 }}>
                      {(formData.status === 'in_que' || formData.status === 'ops_hold') && (
                        <>
                          <div onClick={() => { setShowRejectModal(true); setShowOpsDropdown(false); }} style={{ ...dropdownItemStyle, color: '#ef4444', fontWeight: 'bold' }}>
                            Reject Order
                          </div>
                          {formData.status !== 'ops_hold' && (
                              <div onClick={() => { setShowHoldModal(true); setShowOpsDropdown(false); }} style={{ ...dropdownItemStyle, color: '#f59e0b', fontWeight: 'bold' }}>
                                Hold Order
                              </div>
                          )}
                        </>
                      )}
                      <div onClick={() => { handleExportPdf(); setShowOpsDropdown(false); }} style={dropdownItemStyle}>
                        <FileText size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'text-bottom' }} /> Export PDF
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <>
          <div style={cardStyle}>
            <h3>Order Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
                <div><label style={labelStyle}>Date</label><input type="date" name="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} style={inputStyle} disabled={!isEditingAllowed} /></div>
                <div><label style={labelStyle}>From (Firm)</label><select name="firmId" value={formData.firmId} onChange={handleFirmChange} style={inputStyle} disabled={!isEditingAllowed}><option value="">Select...</option>{firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
                <div><label style={labelStyle}>Salesperson</label><select name="salesPerson" value={formData.salesPerson} onChange={(e) => setFormData({...formData, salesPerson: e.target.value})} style={inputStyle} disabled={!isEditingAllowed}><option value="">Select...</option>{config.sales_team.map(n => <option key={n} value={n}>{n}</option>)}</select></div>
                <div><label style={labelStyle}>Order Type</label><select name="orderType" value={formData.orderType} onChange={(e) => setFormData({...formData, orderType: e.target.value})} style={inputStyle} disabled={!isEditingAllowed}><option>Sales</option><option>Rental</option></select></div>
            </div>
          </div>

       <div style={{...cardStyle, marginTop: '20px'}}>
            <h3>Customer Details</h3>
           <div style={{ marginBottom: selectedCustomer ? '20px' : '0' }}>
              <label style={labelStyle}><User size={12}/> Select Customer</label>
              <div style={{ position: 'relative' }} ref={customerSearchRef}>
                <input 
                  placeholder="Search by Display Name..." 
                  value={customerSearchTerm}
                  onFocus={() => { if(isEditingAllowed) { setFilteredCustomers(allCustomers); setShowCustSuggestions(true); }}}
                  onChange={(e) => handleCustomerSearch(e.target.value)}
                  style={{ ...inputStyle, padding: '12px', fontSize: '15px' }}
                  autoComplete="off"
                  disabled={!isEditingAllowed}
                />
                {showCustSuggestions && isEditingAllowed && (
                  <div style={dropdownStyle}>
                    {filteredCustomers.map(cust => (
                      <div key={cust.id} onClick={() => selectCustomer(cust)} style={{...dropdownItemStyle, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <span><strong>{cust.display_name}</strong> - {cust.company_name}</span>
                        {formData.customerID === cust.id && (
                          <button onClick={(e) => { e.stopPropagation(); setShowModal(true); setCustomerDataToEdit(cust); }} style={linkBtnStyle}>
                            <Edit2 size={12}/> Edit
                          </button>
                        )}
                      </div>
                    ))}
                    <div onClick={() => { setShowModal(true); setCustomerDataToEdit(null); setShowCustSuggestions(false); }} style={{ ...dropdownItemStyle, color: '#2563eb', fontWeight: 'bold', borderTop: '1px solid #e5e7eb', display:'flex', alignItems:'center', gap:5 }}>
                      <Plus size={16} /> Add New Customer
                    </div>
                  </div>
                )}
              </div>
           </div>

           {selectedCustomer && (
             <>
               <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5, borderTop:'1px solid #e5e7eb', paddingTop:'20px'}}>
                 <h4 style={{margin:0, fontSize:'16px'}}>Customer: {selectedCustomer.display_name}</h4>
                 <button onClick={() => { setShowModal(true); setCustomerDataToEdit(selectedCustomer); }} style={linkBtnStyle}>
                   <Edit2 size={12}/> Edit Details
                 </button>
               </div>
               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', paddingTop:'20px', animation: 'fadeIn 0.3s' }}>
                  <div><label style={labelStyle}>Project Name</label><input type="text" name="projectName" value={formData.projectName} onChange={(e) => setFormData(p => ({...p, projectName: e.target.value}))} style={inputStyle} disabled={!isEditingAllowed} /></div>
                  <div><label style={labelStyle}>Project Location</label><input type="text" name="projectLocation" value={formData.projectLocation?.address || ''} onChange={(e) => setFormData(p => ({...p, projectLocation: {...p.projectLocation, address: e.target.value}}))} style={inputStyle} disabled={!isEditingAllowed} /></div>
               </div>

               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', paddingTop:'20px', animation: 'fadeIn 0.3s' }}>
                  <div>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                       <label style={{...labelStyle, marginBottom:0}}><MapPin size={12}/> Billing Address</label>
                       {isEditingAllowed && formData.billingAddress && (
                         <button onClick={() => { setAddressDataToEdit(formData.billingAddress); setShowAddressModal(true); setAddressModalType('billing'); }} style={linkBtnStyle}><Edit2 size={12}/> Edit</button>
                       )}
                    </div>
                    
                    <select 
                      value={formData.billingAddress?.id || ''} 
                      onChange={(e) => handleAddressSelect('billing', e.target.value)}
                      style={{...inputStyle, borderColor: '#2563eb', backgroundColor:'#eff6ff'}}
                      disabled={!isEditingAllowed}
                    >
                        <option value="">-- Select Address --</option>
                        {selectedCustomer?.addresses?.filter(a => a.address_type === 'Billing').map(addr => (
                              <option key={addr.id} value={addr.id}>{addr.attention || addr.city}</option>
                        ))}
                        <option value="new">+ Add New Address</option>
                    </select>
                    {formData.billingAddress && <div style={{...readOnlyBoxStyle, marginTop: '10px'}}>{formatAddress(formData.billingAddress)}</div>}

                    <div style={{marginTop: 15}}>
                       <label style={labelStyle}>Purchaser Details</label>
                       <div style={{fontSize:13, color:'#374151', display:'flex', flexDirection:'column', gap:5}}>
                          {formData.billingAddress?.attention && (
                            <div style={{display:'flex', gap:5, alignItems:'center', fontWeight:'bold'}}>
                                <UserCheck size={12} color="#4b5563"/> 
                                {formData.billingAddress.attention}
                            </div>
                          )}
                          <div style={{display:'flex', gap:5, alignItems:'center'}}>
                              <Phone size={12} color="#4b5563"/> 
                              {formData.billingAddress?.phone || selectedCustomer.mobile || '--'}
                          </div>
                          <div style={{display:'flex', gap:5, alignItems:'center'}}>
                              <Mail size={12} color="#4b5563"/> 
                              {formData.billingAddress?.email || selectedCustomer.email || '--'}
                          </div>
                       </div>
                    </div>
                  </div>

                  <div>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                       <label style={{...labelStyle, marginBottom:0}}><MapPin size={12}/> Shipping Address</label>
                       {isEditingAllowed && formData.shippingAddress && (
                         <button onClick={() => { setAddressDataToEdit(formData.shippingAddress); setShowAddressModal(true); setAddressModalType('shipping'); }} style={linkBtnStyle}><Edit2 size={12}/> Edit</button>
                       )}
                    </div>

                    <select 
                      value={formData.shippingAddress?.id || ''} 
                      onChange={(e) => handleAddressSelect('shipping', e.target.value)}
                      style={{...inputStyle, borderColor: '#2563eb', backgroundColor:'#eff6ff'}}
                      disabled={!isEditingAllowed}
                    >
                        <option value="">-- Select Address --</option>
                        {selectedCustomer?.addresses?.filter(a => a.address_type === 'Shipping').map(addr => (
                              <option key={addr.id} value={addr.id}>{addr.attention || addr.city}</option>
                        ))}
                        <option value="new">+ Add New Address</option>
                    </select>
                    {formData.shippingAddress && <div style={{...readOnlyBoxStyle, marginTop: '10px'}}>{formatAddress(formData.shippingAddress)}</div>}

                    <div style={{marginTop: 15}}>
                       <label style={labelStyle}>Site Person Details</label>
                       <div style={{fontSize:13, color:'#374151', display:'flex', flexDirection:'column', gap:5}}>
                          {(formData.shippingAddress?.attention || selectedCustomer.primary_contact?.first_name) && (
                              <div style={{display:'flex', gap:5, alignItems:'center', fontWeight:'bold'}}>
                                  <UserCheck size={12} color="#4b5563"/> 
                                  {formData.shippingAddress?.attention || `${selectedCustomer.primary_contact?.first_name || ''} ${selectedCustomer.primary_contact?.last_name || ''}`}
                              </div>
                          )}
                          <div style={{display:'flex', gap:5, alignItems:'center'}}>
                              <Phone size={12} color="#4b5563"/> 
                              {formData.shippingAddress?.phone || selectedCustomer.mobile || selectedCustomer.work_phone || '--'}
                          </div>
                          <div style={{display:'flex', gap:5, alignItems:'center'}}>
                              <Mail size={12} color="#4b5563"/> 
                              {formData.shippingAddress?.email || selectedCustomer.email || '--'}
                          </div>
                       </div>
                    </div>
                  </div>
               </div>
             </>
           )}
      </div>

        <div style={{...cardStyle, marginTop: '20px'}}>
            <h3>{formData.orderType} Order Details</h3>
            {formData.orderType === 'Sales' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                    <div>
                        <label style={labelStyle}>Quote Number</label>
                        <input type="text" name="quoteNumber" value={formData.quoteNumber} onChange={(e) => setFormData({...formData, quoteNumber: e.target.value})} style={inputStyle} disabled={!isEditingAllowed} />
                    </div>
                    <div>
                        <label style={labelStyle}>Quote Date</label>
                        <input type="date" name="quoteDate" value={formData.quoteDate} onChange={(e) => setFormData({...formData, quoteDate: e.target.value})} style={inputStyle} disabled={!isEditingAllowed} />
                    </div>
                    {renderFileUpload('quoteFileUrl', 'Upload Quote')}
                    <div>
                        <label style={labelStyle}>PO Number</label>
                        <input type="text" name="poNumber" value={formData.poNumber} onChange={(e) => setFormData({...formData, poNumber: e.target.value})} style={inputStyle} disabled={!isEditingAllowed} />
                    </div>
                    <div>
                        <label style={labelStyle}>PO Date</label>
                        <input type="date" name="poDate" value={formData.poDate} onChange={(e) => setFormData({...formData, poDate: e.target.value})} style={inputStyle} disabled={!isEditingAllowed} />
                    </div>
                    {renderFileUpload('poFileUrl', 'Upload PO')}
                    <div>
                        <label style={labelStyle}>Order Value</label>
                        <input type="number" name="orderValue" value={formData.orderValue} onChange={(e) => setFormData({...formData, orderValue: parseFloat(e.target.value)})} style={inputStyle} disabled={!isEditingAllowed} />
                    </div>
                    <div>
                        <label style={labelStyle}>Payment Status</label>
                        <select name="paymentStatus" value={formData.paymentStatus} onChange={(e) => setFormData({...formData, paymentStatus: e.target.value})} style={inputStyle} disabled={!isEditingAllowed}>
                            <option>RECEIVED</option>
                            <option>ON DISPATCH</option>
                            <option>CREDIT (NET 7)</option>
                            <option>CREDIT (NET 15)</option>
                            <option>CREDIT (NET 30)</option>
                            <option>CREDIT (NET 45)</option>
                            <option>CREDIT (NET 60)</option>
                            <option>CREDIT (NET 90)</option>
                        </select>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                    <div style={{gridColumn: 'span 3', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px'}}>
                        <div>
                            <label style={labelStyle}>Quote Number</label>
                            <input type="text" name="quoteNumber" value={formData.quoteNumber} onChange={(e) => setFormData({...formData, quoteNumber: e.target.value})} style={inputStyle} disabled={!isEditingAllowed} />
                        </div>
                        <div>
                            <label style={labelStyle}>Quote Date</label>
                            <input type="date" name="quoteDate" value={formData.quoteDate} onChange={(e) => setFormData({...formData, quoteDate: e.target.value})} style={inputStyle} disabled={!isEditingAllowed} />
                        </div>
                        {renderFileUpload('quoteFileUrl', 'Upload Quote')}
                        <div>
                            <label style={labelStyle}>Work Order Number</label>
                            <input type="text" name="workOrderNumber" value={formData.workOrderNumber} onChange={(e) => setFormData({...formData, workOrderNumber: e.target.value})} style={inputStyle} disabled={!isEditingAllowed} />
                        </div>
                        <div>
                            <label style={labelStyle}>Work Order Date</label>
                            <input type="date" name="woDate" value={formData.woDate} onChange={(e) => setFormData({...formData, woDate: e.target.value})} style={inputStyle} disabled={!isEditingAllowed} />
                         </div>
                        {renderFileUpload('workOrderFileUrl', 'Upload Work Order')}
                    </div>

                    <div style={{gridColumn: 'span 3', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', borderTop: '1px solid #eee', paddingTop: '15px'}}>
                        <div>
                            <input type="checkbox" id="rentalAgreementReceived" name="rentalAgreementReceived" checked={formData.rentalAgreementReceived} onChange={(e) => setFormData({...formData, rentalAgreementReceived: e.target.checked})} style={{ marginRight: '10px' }} disabled={!isEditingAllowed} />
                            <label htmlFor="rentalAgreementReceived" style={{ fontSize: '14px', fontWeight: 'normal', color: '#374151' }}>Rental Agreement Received</label>
                            {formData.rentalAgreementReceived && (
                                <div style={{marginTop: '10px'}}>
                                    {renderFileUpload('rentalAgreementFileUrl', 'Upload Rental Agreement')}
                                </div>
                            )}
                        </div>

                        <div>
                            <input type="checkbox" id="backgroundVerificationDone" name="backgroundVerificationDone" checked={formData.backgroundVerificationDone} onChange={(e) => setFormData({...formData, backgroundVerificationDone: e.target.checked})} style={{ marginRight: '10px' }} disabled={!isEditingAllowed} />
                            <label htmlFor="backgroundVerificationDone" style={{ fontSize: '14px', fontWeight: 'normal', color: '#374151' }}>Background Verification Done</label>
                            {formData.backgroundVerificationDone && (
                                <div style={{marginTop: '10px'}}>
                                    <label style={labelStyle}>Done By</label>
                                    <select name="backgroundVerificationDoneBy" value={formData.backgroundVerificationDoneBy} onChange={(e) => setFormData({...formData, backgroundVerificationDoneBy: e.target.value})} style={inputStyle} disabled={!isEditingAllowed}>
                                        <option value="">Select...</option>
                                        <option>Nitin Ekbote</option>
                                        <option>Satish Pawar</option>
                                        <option>Rohit Soni</option>
                                        <option>Ghanshyam</option>
                                        <option>Rajesh</option>
                                        <option>Ankit</option>
                                        <option>Parmesh</option>
                                        <option>Pranav Machhi</option>
                                        <option>Shahid Ali</option>
                                        <option>Deepak Yadav</option>

                                    </select>
                                </div>
                            )}
                        </div>
                        
                        <div>
                            <input type="checkbox" id="securityChequeReceived" name="securityChequeReceived" checked={formData.securityChequeReceived} onChange={(e) => setFormData({...formData, securityChequeReceived: e.target.checked})} style={{ marginRight: '10px' }} disabled={!isEditingAllowed} />
                            <label htmlFor="securityChequeReceived" style={{ fontSize: '14px', fontWeight: 'normal', color: '#374151' }}>Security Cheque Received</label>
                            {formData.securityChequeReceived && (
                                <div style={{marginTop: '10px'}}>
                                    {renderFileUpload('securityChequeFileUrl', 'Upload Security Cheque')}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{gridColumn: 'span 3', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', borderTop: '1px solid #eee', paddingTop: '15px'}}>
                        <div>
                            <label style={labelStyle}>Rental Tenure in Months</label>
                            <input type="number" name="rentalTenureMonths" value={formData.rentalTenureMonths} onChange={(e) => setFormData({...formData, rentalTenureMonths: parseFloat(e.target.value)})} style={inputStyle} disabled={!isEditingAllowed} />
                        </div>
                        <div>
                            <label style={labelStyle}>Order Value</label>
                            <input type="number" name="orderValue" value={formData.orderValue} onChange={(e) => setFormData({...formData, orderValue: parseFloat(e.target.value)})} style={inputStyle} disabled={!isEditingAllowed} />
                        </div>
                        <div>
                            <label style={labelStyle}>Payment Status</label>
                            <select name="paymentStatus" value={formData.paymentStatus} onChange={(e) => setFormData({...formData, paymentStatus: e.target.value})} style={inputStyle} disabled={!isEditingAllowed}>
                                <option>RECEIVED</option>
                                <option>ON DISPATCH</option>
                                <option>CREDIT (NET 7)</option>
                                <option>CREDIT (NET 15)</option>
                                <option>CREDIT (NET 30)</option>
                                <option>CREDIT (NET 45)</option>
                                <option>CREDIT (NET 60)</option>
                                <option>CREDIT (NET 90)</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginTop: '20px' }}>
            <div style={cardStyle}>
                <h3>Logistics</h3>
                <textarea 
                    name="logisticsDetails" 
                    value={formData.logisticsDetails} 
                    onChange={(e) => setFormData({...formData, logisticsDetails: e.target.value})} 
                    placeholder="Fill logistic details & If For/Self pickup kindly mention the amount" 
                    style={{...inputStyle, minHeight: '80px', resize: 'vertical'}} 
                    disabled={!isEditingAllowed}
                />
            </div>

            <div style={cardStyle}>
                <h3>Installation Details</h3>
                <textarea 
                    name="installationDetails" 
                    value={formData.installationDetails} 
                    onChange={(e) => setFormData({...formData, installationDetails: e.target.value})} 
                    placeholder="Please input installation details." 
                    style={{...inputStyle, minHeight: '80px', resize: 'vertical'}} 
                    disabled={!isEditingAllowed}
                />
            </div>

            <div style={cardStyle}>
                <h3>Material Checklist</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151' }}>
                        <input type="checkbox" checked={materialChecklistState.stickers} onChange={(e) => setMaterialChecklistState({...materialChecklistState, stickers: e.target.checked})} style={{ marginRight: '10px' }} disabled={!isEditingAllowed} />
                        Stickers
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151' }}>
                        <input type="checkbox" checked={materialChecklistState.laserMarking} onChange={(e) => setMaterialChecklistState({...materialChecklistState, laserMarking: e.target.checked})} style={{ marginRight: '10px' }} disabled={!isEditingAllowed} />
                        Laser Marking
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151' }}>
                        <input type="checkbox" checked={materialChecklistState.materialWrapping} onChange={(e) => setMaterialChecklistState({...materialChecklistState, materialWrapping: e.target.checked})} style={{ marginRight: '10px' }} disabled={!isEditingAllowed} />
                        Material Wrapping
                    </label>
                </div>
                <textarea 
                    name="materialInstructions" 
                    value={materialChecklistState.materialInstructions} 
                    onChange={(e) => setMaterialChecklistState({...materialChecklistState, materialInstructions: e.target.value})} 
                    placeholder="Please add any additional instructions here" 
                    style={{...inputStyle, minHeight: '80px', resize: 'vertical'}} 
                    disabled={!isEditingAllowed}
                />
            </div>
        </div>

        <div style={{...cardStyle, marginTop: '20px'}}>
            <h3>Bill of Material Generator</h3>
            {boms.map((bom, index) => (
                <div key={bom.id} style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '10px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0 }}>{bom.name}</h4>
                        <div>
                            {isEditingAllowed && <button onClick={() => {
                                setCurrentBom(bom);
                                setShowBomModal(true);
                            }} style={{...linkBtnStyle, marginRight: '10px'}}><Edit2 size={14}/> Edit</button>}
                            {isEditingAllowed && <button onClick={() => {
                                if (window.confirm("Are you sure you want to delete this Bill of Material (BOM)? This will remove all its components from the order.")) {
                                    setBoms(boms.filter(b => b.id !== bom.id));
                                }
                            }} style={{...linkBtnStyle, color: '#ef4444'}}><Trash2 size={14}/> Delete</button>}
                        </div>
                    </div>
                    <p style={{fontSize: '12px', color: '#6b7280'}}>{bom.components.length} components</p>
                </div>
            ))}
            {isEditingAllowed && <button onClick={() => {
                setCurrentBom({ id: Date.now(), name: `BOM ${boms.length + 1}`, components: [], multiplier: 1 });
                setShowBomModal(true);
            }} style={btnPrimaryStyle}>+ Add New BOM</button>}
        </div>
        </>
      ) : (
        <>
          {renderPreviewContent()}
          
          {/* --- VIEW-ONLY ATTACHMENTS SECTION (For Ops/Sales) --- */}
          {(formData.quoteFileUrl || formData.poFileUrl || formData.workOrderFileUrl || formData.rentalAgreementFileUrl || formData.securityChequeFileUrl) && (
            <div style={{...cardStyle, marginTop: '20px', maxWidth: '1000px', margin: isMobile ? '20px 0' : '20px auto', animation: 'fadeIn 0.3s'}}>
              <h3 style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px'}}>
                <Paperclip size={20} color="#2563eb"/> Document Attachments
              </h3>
              <div style={{display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '15px'}}>
                {formData.quoteFileUrl && (
                  <a href={formData.quoteFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                    <div style={attachmentIconStyle}><FileText size={20}/></div>
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={attachmentLabelStyle}>Quote Document</div>
                      <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.quoteFileUrl)}</div>
                    </div>
                    <Eye size={18} color="#6b7280"/>
                  </a>
                )}
                {formData.poFileUrl && (
                  <a href={formData.poFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                    <div style={{...attachmentIconStyle, backgroundColor: '#dcfce7', color: '#166534'}}><FileText size={20}/></div>
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={attachmentLabelStyle}>Purchase Order (PO)</div>
                      <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.poFileUrl)}</div>
                    </div>
                    <Eye size={18} color="#6b7280"/>
                  </a>
                )}
                {formData.workOrderFileUrl && (
                  <a href={formData.workOrderFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                    <div style={{...attachmentIconStyle, backgroundColor: '#fef9c3', color: '#854d0e'}}><FileText size={20}/></div>
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={attachmentLabelStyle}>Work Order</div>
                      <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.workOrderFileUrl)}</div>
                    </div>
                    <Eye size={18} color="#6b7280"/>
                  </a>
                )}
                {formData.rentalAgreementFileUrl && (
                  <a href={formData.rentalAgreementFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                    <div style={{...attachmentIconStyle, backgroundColor: '#ede9fe', color: '#5b21b6'}}><FileText size={20}/></div>
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={attachmentLabelStyle}>Rental Agreement</div>
                      <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.rentalAgreementFileUrl)}</div>
                    </div>
                    <Eye size={18} color="#6b7280"/>
                  </a>
                )}
                {formData.securityChequeFileUrl && (
                  <a href={formData.securityChequeFileUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                    <div style={{...attachmentIconStyle, backgroundColor: '#fee2e2', color: '#991b1b'}}><FileText size={20}/></div>
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={attachmentLabelStyle}>Security Cheque</div>
                      <div style={attachmentNameStyle}>{getFileNameFromUrl(formData.securityChequeFileUrl)}</div>
                    </div>
                    <Eye size={18} color="#6b7280"/>
                  </a>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* FOOTER TIMELINE SECTION */}
      {!isEditing && (
        <div style={{ 
          marginTop: '40px', 
          maxWidth: '1000px', 
          margin: isMobile ? '30px 0' : '50px auto 20px auto',
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <h3 style={{ 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: '#1e293b', 
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            borderBottom: '2px solid #f1f5f9',
            paddingBottom: '10px'
          }}>
            <Clock size={20} color="#2563eb" /> Order Progress Trackers
          </h3>

          {dispatches.length === 0 ? (
            <HorizontalTimeline 
              orderData={formData} 
              title="Order Progress" 
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {dispatches.map((dispatch, idx) => (
                <HorizontalTimeline 
                  key={dispatch.id}
                  orderData={formData} 
                  dispatchData={dispatch} 
                  title={`Shipment Tracker: ${dispatch.mocNo}`} 
                />
              ))}
            </div>
          )}
        </div>
      )}

      {showBomModal && (
        <div style={modalOverlayStyle}>
          <div style={{...modalContentStyle, width:'85vw', maxWidth: '1400px', height: '90vh'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', paddingBottom: '15px', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px' }}>BOM Editor: <span style={{color: '#2563eb'}}>{currentBom.name}</span></h2>
              <button onClick={() => setShowBomModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>

            <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '380px 1fr', gap: '20px', overflow: 'visible' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto', padding: '5px', minHeight: '400px' }}>
                  <div style={{...cardStyle, padding: '15px'}}>
                      <label style={labelStyle}>BOM Name</label>
                      <input type="text" value={currentBom.name} onChange={(e) => setCurrentBom({...currentBom, name: e.target.value})} style={inputStyle} disabled={!isEditingAllowed} />
                  </div>
                  
                  <div style={{...cardStyle, padding: '15px'}}>
                      <label style={labelStyle}>Search Presets/Components</label>
                      <div style={{ position: 'relative' }} ref={bomSearchRef}>
                          <input type="text" placeholder="Search by name or SKU..." value={bomSearchTerm} 
                              onFocus={() => { if(isEditingAllowed) setShowBomSearchSuggestions(true); }}
                              onChange={(e) => {
                                  setBomSearchTerm(e.target.value);
                                  if (e.target.value.length > 1) {
                                      setBomFilteredProducts(bomDatabase.filter(p => 
                                          p.name.toLowerCase().includes(e.target.value.toLowerCase()) || 
                                          p.sku.toLowerCase().includes(e.target.value.toLowerCase())
                                      ));
                                      setShowBomSearchSuggestions(true);
                                  } else {
                                      setBomFilteredProducts([]);
                                      setShowBomSearchSuggestions(false);
                                  }
                              }} style={inputStyle} disabled={!isEditingAllowed} />
                          {(bomFilteredProducts.length > 0 && showBomSearchSuggestions && isEditingAllowed) && (
                              <div style={{...dropdownStyle, maxHeight: '300px', width: '100%', zIndex: 1000}}>
                                  {bomFilteredProducts.map(p => (
                                      <div key={p.sku} style={dropdownItemStyle} onClick={() => {
                                          let componentsToAdd = [];
                                          if (p.type === 'preset') {
                                              componentsToAdd = p.presetComponents.map(pc => {
                                                  const component = bomDatabase.find(c => c.sku === pc.sku);
                                                  return {...component, baseQty: pc.qty, id: `${p.sku}-${pc.sku}-${Date.now()}`};
                                              });
                                          } else {
                                              componentsToAdd.push({...p, baseQty: 1, id: `${p.sku}-${Date.now()}`});
                                          }

                                          const newComponents = [...currentBom.components];
                                          componentsToAdd.forEach(newComp => {
                                            const existingIndex = newComponents.findIndex(c => c.sku === newComp.sku);
                                            if (existingIndex > -1) {
                                              newComponents[existingIndex].baseQty += newComp.baseQty;
                                            } else {
                                              newComponents.push(newComp);
                                            }
                                          });

                                          setCurrentBom({...currentBom, components: newComponents});
                                          setBomSearchTerm('');
                                          setBomFilteredProducts([]);
                                          setShowBomSearchSuggestions(false);
                                      }}>
                                          <strong>{p.name}</strong> ({p.sku}) - <span style={{textTransform: 'capitalize', color: '#6b7280', fontSize:'12px'}}>{p.type}</span>
                                      </div>
                                  ))}
                              </div>
                          )}
                      </div>
                  </div>

                  <div style={{...cardStyle, padding: '15px'}}>
                      <label style={labelStyle}>Multiplier</label>
                      <input 
                          type="number" 
                          value={currentBom.multiplier}
                          min="0"
                          onChange={(e) => setCurrentBom({...currentBom, multiplier: parseFloat(e.target.value) || 1})}
                          style={inputStyle}
                          disabled={!isEditingAllowed}
                      />
                      <p style={{fontSize: 12, color: '#6b7280', marginTop: 8, lineHeight: '1.5'}}>Multiplier will be applied to the base quantity of all components in this BOM.</p>
                  </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', overflow:'hidden' }}>
                  <div style={{ padding: '12px 15px', borderBottom: '1px solid #e5e7eb', background: 'white' }}>
                      <h4 style={{ margin: 0, fontSize: '16px' }}>Components ({currentBom.components.length})</h4>
                  </div>

                  {currentBom.components.length > 0 ? (
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{position: 'sticky', top: 0, background: '#f9fafb'}}>
                                <tr>
                                    <th style={{...thStyle, textAlign: 'left', paddingLeft: '15px'}}>Name</th>
                                    <th style={{...thStyle, width: '120px'}}>SKU</th>
                                    <th style={{...thStyle, width: '120px'}}>Base Qty</th>
                                    <th style={{...thStyle, width: '120px'}}>Total Qty</th>
                                    <th style={{...thStyle, width: '80px'}}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentBom.components.map((c, index) => (
                                    <tr key={c.id || index} style={{borderBottom: '1px solid #e5e7eb'}}>
                                        <td style={{...tdStyle, textAlign: 'left', paddingLeft: '15px'}}>
                                            <span style={{fontWeight: '500'}}>{c.name}</span>
                                        </td>
                                        <td style={{...tdStyle, color: '#6b7280'}}>{c.sku}</td>
                                        <td style={tdStyle}>
                                            <input type="number" value={c.baseQty} min="0" style={{...inputStyle, textAlign: 'center', width: '70px', padding: '6px'}} onChange={(e) => {
                                                const newComponents = [...currentBom.components];
                                                newComponents[index].baseQty = parseInt(e.target.value, 10) || 0;
                                                setCurrentBom({...currentBom, components: newComponents});
                                            }} disabled={!isEditingAllowed} />
                                        </td>
                                        <td style={{...tdStyle, fontWeight: 'bold', fontSize: '14px'}}>
                                            {Math.round((c.baseQty * currentBom.multiplier) * 100) / 100}
                                        </td>
                                        <td style={tdStyle}>
                                            {isEditingAllowed && <button onClick={() => {
                                                const newComponents = currentBom.components.filter((_, i) => i !== index);
                                                setCurrentBom({...currentBom, components: newComponents});
                                            }} style={{...linkBtnStyle, color: '#ef4444'}}><Trash2 size={16}/></button>}
                                        </td>
                                    </tr>
                                ))}
                                <tr style={{backgroundColor: '#f9fafb', fontWeight: 'bold'}}>
                                    <td colSpan="3" style={{...tdStyle, textAlign: 'right', paddingRight: '15px'}}>Total Quantity:</td>
                                    <td style={{...tdStyle, fontSize: '14px'}}>
                                        {currentBom.components.reduce((sum, c) => sum + Math.round((c.baseQty || 0) * currentBom.multiplier), 0)}
                                    </td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                  ) : (
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', flexDirection: 'column', gap: '10px' }}>
                          <FileText size={40} color="#9ca3af" />
                          <h4 style={{margin:0}}>No components yet</h4>
                          <p style={{margin:0, fontSize: '13px', maxWidth: '250px', textAlign: 'center', lineHeight: '1.5'}}>Search for presets or individual components to add them to the list.</p>
                      </div>
                  )}
              </div>
            </div>

            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 20, paddingTop: 15, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
               <button onClick={() => setShowBomModal(false)} style={{ padding: '10px 20px', border: '1px solid #d1d5db', background: 'white', borderRadius: 6, cursor:'pointer', fontWeight: 500 }}>Cancel</button>
               {isEditingAllowed && <button onClick={() => {
                   const bomExists = boms.some(b => b.id === currentBom.id);
                   if (bomExists) {
                       setBoms(boms.map(b => b.id === currentBom.id ? currentBom : b));
                   } else {
                       setBoms([...boms, currentBom]);
                   }
                   setShowBomModal(false);
                   setCurrentBom(null);
               }} style={btnPrimaryStyle}>Save BOM</button>}
            </div>
          </div>
        </div>
      )}

      <NewCustomerModal 
        show={showModal} 
        onClose={() => { setShowModal(false); setCustomerDataToEdit(null); }} 
        onSave={(savedCust) => {
          setAllCustomers(prev => {
            const existingIndex = prev.findIndex(c => c.id === savedCust.id);
            if (existingIndex > -1) {
              return prev.map(c => c.id === savedCust.id ? savedCust : c);
            }
            return [...prev, savedCust];
          });
          selectCustomer(savedCust);
        }}
        db={db}
        customerData={customerDataToEdit}
        isMobile={isMobile}
      />

      <AddAddressModal
        show={showAddressModal}
        onClose={() => { setShowAddressModal(false); setAddressDataToEdit(null); }}
        onSave={(savedAddress, updatedAddresses) => {
          const updatedCustomer = { ...selectedCustomer, addresses: updatedAddresses };
          setSelectedCustomer(updatedCustomer);
          setAllCustomers(prev => prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c));
          if (savedAddress.address_type === 'Billing') {
            setFormData(prev => ({ ...prev, billingAddress: savedAddress }));
          } else {
            setFormData(prev => ({ ...prev, shippingAddress: savedAddress }));
          }
        }}
        db={db}
        customer={selectedCustomer}
        addressType={addressModalType}
        addressData={addressDataToEdit}
        isMobile={isMobile}
      />


      {/* REJECTION MODAL */}
      {showRejectModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '400px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#dc2626' }}>Reject Order</h3>
              <button onClick={() => { setShowRejectModal(false); setRejectionReason(''); setOtherReason(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
              Please select a reason for rejecting Order <strong>#{formData.orderNumber || id}</strong>. This will return the order to the Sales team.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {["Short Stock", "Client Cancelled", "Duplicate Order", "PO Mismatch", "Other"].map((reason) => (
                <label key={reason} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="rejectionReason" 
                    value={reason} 
                    checked={rejectionReason === reason} 
                    onChange={(e) => setRejectionReason(e.target.value)} 
                  />
                  {reason}
                </label>
              ))}
              
              {rejectionReason === 'Other' && (
                <textarea 
                  placeholder="Please specify the reason..." 
                  value={otherReason} 
                  onChange={(e) => setOtherReason(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minHeight: '80px', marginTop: '10px', boxSizing: 'border-box' }}
                />
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => { setShowRejectModal(false); setRejectionReason(''); setOtherReason(''); }} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontWeight: 'bold', color: '#374151' }}>
                Cancel
              </button>
              <button onClick={handleRejectSubmit} disabled={isRejecting} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isRejecting ? <Loader2 className="animate-spin" size={16} /> : <X size={16} />}
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HOLD MODAL */}
      {showHoldModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '400px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#f59e0b' }}>Hold Order</h3>
              <button onClick={() => { setShowHoldModal(false); setHoldReason(''); setOtherHoldReason(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
              Please select a reason for placing Order <strong>#{formData.orderNumber || id}</strong> on hold. It will remain in your queue.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {["Awaiting Stock", "Awaiting Client Confirmation", "Pending Document", "Other"].map((reason) => (
                <label key={reason} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="holdReason" 
                    value={reason} 
                    checked={holdReason === reason} 
                    onChange={(e) => setHoldReason(e.target.value)} 
                  />
                  {reason}
                </label>
              ))}
              
              {holdReason === 'Other' && (
                <textarea 
                  placeholder="Please specify the hold reason..." 
                  value={otherHoldReason} 
                  onChange={(e) => setOtherHoldReason(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minHeight: '80px', marginTop: '10px', boxSizing: 'border-box' }}
                />
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => { setShowHoldModal(false); setHoldReason(''); setOtherHoldReason(''); }} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontWeight: 'bold', color: '#374151' }}>
                Cancel
              </button>
              <button onClick={handleHoldSubmit} disabled={isHolding} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#f59e0b', color: 'white', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isHolding ? <Loader2 className="animate-spin" size={16} /> : <Clock size={16} />}
                Confirm Hold
              </button>
            </div>
          </div>
        </div>
      )}

      {showFileModal && (
        <div style={modalOverlayStyle}>
          <div style={{...modalContentStyle, width: '500px'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', paddingBottom: '15px', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Manage File: <span style={{color: '#2563eb'}}>{activeFileLabel}</span></h3>
              <button onClick={() => setShowFileModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {formData[activeFileField] ? (
                    <div style={{background: '#f0fdf4', padding: '15px', borderRadius: '8px', border: '1px solid #bbf7d0'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px'}}>
                            <div style={{background: '#22c55e', color: 'white', padding: '8px', borderRadius: '50%'}}><Eye size={20}/></div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <h4 style={{margin: 0, color: '#166534', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={getFileNameFromUrl(formData[activeFileField])}>
                                    {getFileNameFromUrl(formData[activeFileField])}
                                </h4>
                                <p style={{margin: 0, fontSize: '12px', color: '#15803d'}}>You can view or replace this file.</p>
                            </div>
                        </div>
                        <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                            <a 
                                href={formData[activeFileField]} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                style={{
                                    ...btnPrimaryStyle, 
                                    backgroundColor: '#16a34a', 
                                    textDecoration: 'none', 
                                    justifyContent: 'center', 
                                    fontSize: '13px',
                                    flex: 1
                                }}
                            >
                                <Eye size={16}/> View File
                            </a>
                            {isEditingAllowed && (
                                <button 
                                    onClick={() => {
                                        if(window.confirm('Are you sure you want to remove this file?')) {
                                            setFormData({...formData, [activeFileField]: ''});
                                        }
                                    }} 
                                    style={{
                                        ...btnPrimaryStyle, 
                                        backgroundColor: '#ef4444', 
                                        justifyContent: 'center', 
                                        fontSize: '13px',
                                        flex: 1
                                    }}
                                >
                                    <Trash2 size={16}/> Remove
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div style={{textAlign: 'center', padding: '30px 20px', border: '2px dashed #e5e7eb', borderRadius: '8px', background: '#f9fafb'}}>
                        <Paperclip size={40} color="#9ca3af" style={{marginBottom: '10px'}}/>
                        <h4 style={{margin: '0 0 5px 0', color: '#374151'}}>No File Attached</h4>
                        <p style={{margin: 0, fontSize: '13px', color: '#6b7280'}}>Upload a document to attach it to this order.</p>
                    </div>
                )}

                {isEditingAllowed && (
                    <div>
                        <label style={{...labelStyle, fontSize: '12px', marginBottom: '8px'}}>
                            {formData[activeFileField] ? 'Replace with New File' : 'Upload File'}
                        </label>
                        <div style={{position: 'relative'}}>
                            <input 
                                type="file" 
                                onChange={(e) => handleFileUpload(e, activeFileField)} 
                                style={{...inputStyle, padding: '12px', background: '#f9fafb'}} 
                                disabled={uploading} 
                            />
                            {uploading && (
                                <div style={{
                                    position: 'absolute', 
                                    top: 0, left: 0, right: 0, bottom: 0, 
                                    background: 'rgba(255,255,255,0.8)', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    gap: '10px',
                                    fontWeight: 'bold',
                                    color: '#2563eb'
                                }}>
                                    <Loader2 className="animate-spin" size={20} /> Uploading...
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 20, paddingTop: 15, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
               <button onClick={() => { setShowFileModal(false); setActiveFileField(null); }} style={{ padding: '8px 20px', border: '1px solid #d1d5db', background: 'white', borderRadius: 5, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const btnPrimaryStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, maxHeight: '250px', overflowY: 'auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 100 };
const dropdownItemStyle = { padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' };
const modalOverlayStyle = { position: 'fixed', top:0, left:0, right:0, bottom:0, backgroundColor: 'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex: 9999 };
const modalContentStyle = { backgroundColor: 'white', width: '900px', maxWidth:'95%', borderRadius: '8px', padding: '20px', display:'flex', flexDirection:'column', position: 'relative' };
const rowStyle = { display: 'flex', gap: '15px', marginBottom: '15px' };
const sectionHeaderStyle = { margin: '20px 0 10px 0', borderBottom:'1px solid #eee', paddingBottom:5, color:'#374151', fontSize: '14px', fontWeight:'bold' };
const readOnlyBoxStyle = { background: '#f8fafc', padding: '10px', borderRadius: '5px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151', minHeight: '40px' };
const linkBtnStyle = { background:'none', border:'none', color:'#2563eb', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:4, padding:0 };
const thStyle = { padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' };
const tdStyle = { padding: '10px', fontSize: '13px', textAlign: 'center' };

const attachmentCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  textDecoration: 'none',
  color: 'inherit',
  transition: 'all 0.2s'
};

const attachmentIconStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  backgroundColor: '#eff6ff',
  color: '#2563eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const attachmentLabelStyle = {
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151'
};

const attachmentNameStyle = {
  fontSize: '11px',
  color: '#6b7280',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

export default OrderDetail;